// App chrome per the July-2026 mocks: a left sidebar (brand lockup, icon nav,
// promo card) with a slim top-right cluster (theme toggle + avatar menu) over
// the content. The sidebar collapses to an icon rail — the user's choice is
// remembered — and workspace pages (`wide`: review, template builder) drop
// the content width cap and start collapsed so the working area gets the
// space. Small screens use a top tab bar instead; no room for a rail.

import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  Moon,
  Sun,
  BriefcaseBusiness,
  FileText,
  Home,
  LayoutGrid,
  BarChart3,
  Gem,
  PanelLeftClose,
  PanelLeftOpen,
  Settings as SettingsIcon,
} from "lucide-react";
import { useTheme } from "@/lib/theme";
import { useMe } from "@/hooks/useMe";
import { cn } from "@/lib/utils";
import { AvatarMenu } from "@/components/AvatarMenu";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: Home, end: true, adminOnly: false },
  { to: "/resumes", label: "Resumes", icon: FileText, end: false, adminOnly: false },
  { to: "/jds", label: "Job descriptions", icon: BriefcaseBusiness, end: false, adminOnly: false },
  { to: "/templates", label: "Templates", icon: LayoutGrid, end: false, adminOnly: true },
  { to: "/reports", label: "Reports", icon: BarChart3, end: false, adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, end: false, adminOnly: true },
];

function navItemsFor(isAdmin: boolean) {
  return NAV_ITEMS.filter((i) => isAdmin || !i.adminOnly);
}

// Collapse preference. Until the user chooses, regular pages start expanded
// and wide workspace pages start collapsed; one explicit toggle anywhere
// wins everywhere afterwards.
const SIDEBAR_KEY = "agp.sidebar";

function useSidebarCollapsed(defaultCollapsed: boolean) {
  const [stored, setStored] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_KEY);
    } catch {
      return null;
    }
  });
  const collapsed = stored === null ? defaultCollapsed : stored === "collapsed";
  const toggle = () => {
    const next = collapsed ? "expanded" : "collapsed";
    setStored(next);
    try {
      window.localStorage.setItem(SIDEBAR_KEY, next);
    } catch {
      // Preference just won't persist.
    }
  };
  return { collapsed, toggle };
}

function BrandLockup({ markOnly = false }: { markOnly?: boolean }) {
  return (
    <Link
      to="/"
      aria-label="Resume Formatter home"
      className={cn(
        "inline-flex shrink-0 items-center gap-2.5 rounded-sm no-underline hover:bg-surface-2",
        markOnly ? "p-1" : "px-2 py-1",
      )}
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-500 text-text-on-brand">
        <FileText size={19} strokeWidth={1.75} />
      </span>
      {!markOnly && (
        <span className="flex flex-col leading-none">
          <span className="font-display text-[17px] font-bold leading-none tracking-tight text-text">
            Resume Formatter
          </span>
          <span className="mt-0.5 text-[9px] font-medium tracking-wide text-text-2">
            ASCENT GLOBAL PARTNERS
          </span>
        </span>
      )}
    </Link>
  );
}

function Sidebar({
  isAdmin,
  collapsed,
  onToggle,
}: {
  isAdmin: boolean;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const toggleLabel = collapsed ? "Expand sidebar" : "Collapse sidebar";
  return (
    <aside
      className={cn(
        "sticky top-0 hidden h-screen shrink-0 flex-col border-r border-border bg-surface pb-4 pt-4 transition-[width] duration-200 ease-out lg:flex",
        collapsed ? "w-[68px] px-3" : "w-60 px-3",
      )}
    >
      {/* The toggle floats on the sidebar's edge — the 240px rail has no
          spare room for it beside the full lockup. */}
      <button
        type="button"
        onClick={onToggle}
        aria-label={toggleLabel}
        title={toggleLabel}
        className="absolute -right-3.5 top-5 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full border border-border bg-surface text-text-2 shadow-subtle hover:bg-surface-2 hover:text-text"
      >
        {collapsed ? (
          <PanelLeftOpen size={14} strokeWidth={1.75} aria-hidden />
        ) : (
          <PanelLeftClose size={14} strokeWidth={1.75} aria-hidden />
        )}
      </button>
      <div className={cn("flex items-center", collapsed && "justify-center")}>
        <BrandLockup markOnly={collapsed} />
      </div>
      <nav aria-label="Primary" className="mt-8 flex flex-col gap-1">
        {navItemsFor(isAdmin).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-[14px] font-medium no-underline transition-colors",
                collapsed ? "h-10 justify-center" : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-brand-tint font-semibold text-brand-on"
                  : "text-text-2 hover:bg-surface-2 hover:text-text",
              )
            }
          >
            <item.icon size={17} strokeWidth={1.75} aria-hidden />
            {!collapsed && item.label}
          </NavLink>
        ))}
      </nav>
      {!collapsed && (
        <div className="mt-auto rounded-xl border border-border bg-bg p-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-tint text-brand-on">
            <Gem size={18} strokeWidth={1.75} aria-hidden />
          </span>
          <p className="mt-3 text-[13px] font-semibold leading-snug text-text">
            AI-powered resume extraction &amp; formatting
          </p>
          <p className="mt-1 text-[12px] leading-relaxed text-text-2">
            Smart, structured, and ready-to-publish resumes in minutes.
          </p>
        </div>
      )}
    </aside>
  );
}

// Horizontal tabs with the active underline — top-bar nav for small screens.
function TabsNav({ isAdmin }: { isAdmin: boolean }) {
  return (
    <nav aria-label="Primary" className="ml-1 flex items-stretch gap-1 self-stretch overflow-x-auto">
      {navItemsFor(isAdmin).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              "relative inline-flex items-center px-3 text-[14px] font-medium text-text-2 no-underline transition-colors hover:text-text",
              isActive &&
                "font-semibold text-brand-on after:absolute after:inset-x-2 after:bottom-0 after:h-[3px] after:rounded-t-full after:bg-brand-500",
            )
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  const label = isDark ? "Switch to light mode" : "Switch to dark mode";
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={label}
      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-2 hover:bg-surface-2 hover:text-text"
    >
      {isDark ? (
        <Sun className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
      ) : (
        <Moon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
      )}
    </button>
  );
}

export function Shell({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  const me = useMe();
  const isAdmin = me?.role === "admin";
  const sidebar = useSidebarCollapsed(wide);

  return (
    <div className="flex min-h-full">
      <Sidebar isAdmin={isAdmin} collapsed={sidebar.collapsed} onToggle={sidebar.toggle} />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Solid nav bar on small screens; on desktop the sidebar owns nav
            and this collapses to a floating right-aligned cluster. */}
        <header className="sticky top-0 z-30 border-b border-border bg-surface lg:static lg:border-0 lg:bg-transparent">
          <div className="flex h-14 items-center gap-2 px-3 sm:px-4 lg:px-6">
            <div className="flex min-w-0 items-center gap-2 lg:hidden">
              <BrandLockup />
              <TabsNav isAdmin={isAdmin} />
            </div>
            <div className="flex-1" />
            <div className="flex shrink-0 items-center gap-2">
              <ThemeToggle />
              <AvatarMenu />
            </div>
          </div>
        </header>
        <main
          className={cn(
            "w-full flex-1 px-4 pb-6 pt-2 sm:px-6 lg:pt-0",
            !wide && "mx-auto max-w-[1200px]",
          )}
        >
          {children}
        </main>
        <footer className="border-t border-border bg-surface">
          <div className="mx-auto flex max-w-[1200px] items-center justify-between px-4 py-4 text-xs text-text-2 sm:px-6">
            <span>© Ascent Global Partners</span>
            <span>Internal tool — resumes stay private</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
